import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import HelpOutlineOutlined from "@mui/icons-material/HelpOutlineOutlined";
import LinkOffOutlined from "@mui/icons-material/LinkOffOutlined";
import LockOpenOutlined from "@mui/icons-material/LockOpenOutlined";
import { GlobalContext } from "../../context/GlobalContext";
import { getStudioUserToken } from "../../utils/studioAuth";
import { PLATFORM_CONNECT } from "./platformConnectConfig";

function disconnectPlatform(platformId) {
  const prefix = process.env.REACT_APP_TOKEN || "ahead_ai";
  const token =
    getStudioUserToken() ||
    localStorage.getItem(`${prefix}_user`) ||
    sessionStorage.getItem(`${prefix}_user`) ||
    "";
  return fetch(`/api/social-credentials/${platformId}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then((r) => r.json().catch(() => ({})));
}

function LinkPlatformDialog({ open, platform, configured, onClose, onLinked, hitAxios, showSnack, lang }) {
  const [token, setToken] = React.useState("");
  const [linking, setLinking] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setToken("");
      setShowHelp(false);
    }
  }, [open, platform?.id]);

  if (!platform) return null;

  async function handleLink() {
    if (!token.trim()) {
      showSnack(lang?.socialLinkNeedToken || "Paste your connection token first.", false);
      return;
    }
    setLinking(true);
    try {
      const res = await hitAxios({
        path: `/api/social-credentials/${platform.id}/link`,
        post: true,
        obj: { token: token.trim() },
        showLoading: false,
      });
      if (res?.data?.success) {
        showSnack(res.data.msg || lang?.socialLinkSuccess || "Account linked!", true);
        onLinked(platform.id, res.data.data?.display_name);
        onClose();
      } else {
        showSnack(res?.data?.msg || lang?.socialLinkFailed || "Could not link account.", false);
      }
    } finally {
      setLinking(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, pr: 6 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontSize={28} lineHeight={1}>
            {platform.emoji}
          </Typography>
          <Box>
            {configured
              ? lang?.socialLinkUpdateTitle?.replace("{platform}", platform.label) ||
                `Update ${platform.label}`
              : lang?.socialLinkTitle?.replace("{platform}", platform.label) ||
                `Link ${platform.label}`}
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {platform.tagline}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" icon={false} sx={{ py: 1.5 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              {lang?.socialLinkEasySteps || "3 easy steps"}
            </Typography>
            <Stack component="ol" sx={{ m: 0, pl: 2.5 }} spacing={0.75}>
              {platform.steps.map((step, i) => (
                <Typography key={i} component="li" variant="body2">
                  {step}
                </Typography>
              ))}
            </Stack>
          </Alert>

          <TextField
            label={platform.fieldLabel}
            placeholder={platform.fieldPlaceholder}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            autoFocus
            helperText={
              lang?.socialLinkTokenHint ||
              "You can paste plain text, a link, or JSON from your provider."
            }
          />

          <Button
            size="small"
            startIcon={<HelpOutlineOutlined />}
            onClick={() => setShowHelp((v) => !v)}
            sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 600 }}
          >
            {showHelp
              ? lang?.socialLinkHideHelp || "Hide help"
              : lang?.socialLinkShowHelp || "Need more help?"}
          </Button>
          <Collapse in={showHelp}>
            <Typography variant="body2" color="text.secondary">
              {lang?.socialLinkHelpBody ||
                "If you are not sure how to get a token, open the guide for your platform or ask someone who manages your social accounts. You only need to do this once per network."}
            </Typography>
            <Link
              href={platform.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ mt: 1, display: "inline-block", fontWeight: 600 }}
            >
              {platform.helpLinkText}
            </Link>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{lang?.cancel || "Cancel"}</Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={linking || !token.trim()}
          startIcon={linking ? <CircularProgress size={16} color="inherit" /> : <LockOpenOutlined />}
          sx={{ fontWeight: 800, minWidth: 140 }}
        >
          {configured
            ? lang?.socialLinkSave || "Save link"
            : lang?.socialLinkConnect || "Link account"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlatformLinkCard({ platform, configured, displayName, onConnect, onDisconnect, lang }) {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        height: "100%",
        borderColor: configured ? "success.main" : "divider",
        bgcolor: configured ? (theme) => alpha(theme.palette.success.main, 0.04) : undefined,
      }}
    >
      <CardContent sx={{ textAlign: "center", py: 3 }}>
        <Typography fontSize={40} lineHeight={1} sx={{ mb: 1 }}>
          {platform.emoji}
        </Typography>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          {platform.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, minHeight: 32 }}>
          {platform.tagline}
        </Typography>
        {configured ? (
          <Chip
            icon={<CheckCircleOutlined />}
            label={
              displayName
                ? `${lang?.socialLinked || "Linked"} · ${displayName}`
                : lang?.socialLinked || "Linked"
            }
            color="success"
            size="small"
            sx={{ mb: 2, maxWidth: "100%" }}
          />
        ) : (
          <Chip label={lang?.socialNotLinked || "Not linked"} size="small" sx={{ mb: 2 }} />
        )}
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            variant={configured ? "outlined" : "contained"}
            onClick={() => onConnect(platform)}
            sx={{ fontWeight: 700, px: 2.5 }}
          >
            {configured
              ? lang?.socialLinkUpdateBtn || "Update"
              : lang?.socialLinkConnectBtn || "Link account"}
          </Button>
          {configured ? (
            <Button
              variant="text"
              color="error"
              size="small"
              startIcon={<LinkOffOutlined />}
              onClick={() => onDisconnect(platform)}
            >
              {lang?.socialDisconnect || "Disconnect"}
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ConnectAccountsTab({ hitAxios, showSnack }) {
  const { lang } = React.useContext(GlobalContext);
  const [credStatus, setCredStatus] = React.useState([]);
  const [displayNames, setDisplayNames] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [dialogPlatform, setDialogPlatform] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await hitAxios({
        path: "/api/social-credentials",
        post: false,
        showLoading: false,
      });
      if (res?.data?.success) {
        setCredStatus(res.data.data?.platforms || []);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  function statusFor(id) {
    return credStatus.find((s) => s.platform === id) || {};
  }

  function handleLinked(platformId, name) {
    setCredStatus((prev) =>
      prev.map((p) =>
        p.platform === platformId
          ? { ...p, configured: true, updated_at: new Date().toISOString() }
          : p,
      ),
    );
    if (name) setDisplayNames((prev) => ({ ...prev, [platformId]: name }));
    load();
  }

  async function handleDisconnect(platform) {
    const label = platform.label;
    if (
      !window.confirm(
        lang?.socialDisconnectConfirm?.replace("{platform}", label) ||
          `Disconnect ${label}? Scheduled posts to ${label} may fail.`,
      )
    ) {
      return;
    }
    const json = await disconnectPlatform(platform.id);
    if (json.success) {
      showSnack(lang?.socialDisconnected?.replace("{platform}", label) || `${label} disconnected`, true);
      setCredStatus((prev) =>
        prev.map((p) =>
          p.platform === platform.id ? { ...p, configured: false, saved_keys: [] } : p,
        ),
      );
      setDisplayNames((prev) => {
        const next = { ...prev };
        delete next[platform.id];
        return next;
      });
    } else {
      showSnack(json.msg || lang?.socialDisconnectFailed || "Could not disconnect", false);
    }
  }

  const linkedCount = credStatus.filter((p) => p.configured).length;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={1} sx={{ mb: 3, maxWidth: 640 }}>
        <Typography variant="h6" fontWeight={800}>
          {lang?.socialConnectHeadline || "Link your social accounts"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lang?.socialConnectIntro ||
            "Tap a network, paste one connection token, and you are done. No technical setup on your side."}
        </Typography>
        <Chip
          size="small"
          color={linkedCount > 0 ? "success" : "default"}
          label={
            lang?.socialConnectProgress?.replace("{count}", String(linkedCount)) ||
            `${linkedCount} of 4 accounts linked`
          }
          sx={{ alignSelf: "flex-start", fontWeight: 700 }}
        />
      </Stack>

      {linkedCount === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {lang?.socialConnectEmpty ||
            "Link at least one account before scheduling posts. You can link more networks anytime."}
        </Alert>
      ) : linkedCount < 4 ? (
        <Alert severity="success" variant="outlined" sx={{ mb: 3 }}>
          {lang?.socialConnectPartial ||
            "Great start! Link any other networks you want to post to."}
        </Alert>
      ) : (
        <Alert severity="success" sx={{ mb: 3 }}>
          {lang?.socialConnectAll ||
            "All networks are linked. Your scheduled posts can publish automatically."}
        </Alert>
      )}

      <Grid container spacing={2}>
        {PLATFORM_CONNECT.map((platform) => {
          const status = statusFor(platform.id);
          return (
            <Grid item xs={12} sm={6} key={platform.id}>
              <PlatformLinkCard
                platform={platform}
                configured={!!status.configured}
                displayName={displayNames[platform.id]}
                onConnect={setDialogPlatform}
                onDisconnect={handleDisconnect}
                lang={lang}
              />
            </Grid>
          );
        })}
      </Grid>

      <LinkPlatformDialog
        open={Boolean(dialogPlatform)}
        platform={dialogPlatform}
        configured={dialogPlatform ? !!statusFor(dialogPlatform.id).configured : false}
        onClose={() => setDialogPlatform(null)}
        onLinked={handleLinked}
        hitAxios={hitAxios}
        showSnack={showSnack}
        lang={lang}
      />
    </Box>
  );
}
